import {
  type GreatKingdomAction,
  type GreatKingdomPlayerId,
  type GreatKingdomState,
  reduceGameState,
  validateAction,
} from "@cloudnest/great-kingdom-engine";
import { useSyncExternalStore } from "react";
import { createGameApi, type GameEvent } from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";

const gameApi = createGameApi("great-kingdom", "game-platform");

export interface GreatKingdomMultiplayerState {
  readonly gameState: GreatKingdomState | null;
  readonly roomId: string | null;
  readonly mySeatId: GreatKingdomPlayerId | null;
  readonly isHost: boolean;
  readonly lastAppliedSeq: number;
  readonly lastError: string | null;
}

interface ReplayResult {
  readonly gameState: GreatKingdomState | null;
  readonly lastAppliedSeq: number;
}

const initialState: GreatKingdomMultiplayerState = {
  gameState: null,
  roomId: null,
  mySeatId: null,
  isHost: false,
  lastAppliedSeq: 0,
  lastError: null,
};

let state: GreatKingdomMultiplayerState = { ...initialState };
let catchUpInFlight = false;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function getSeatForUser(
  userId: string | null,
  participantUserIds: readonly string[],
): GreatKingdomPlayerId | null {
  if (!userId) return null;
  const index = participantUserIds.indexOf(userId);
  if (index === 0) return "p1";
  if (index === 1) return "p2";
  return null;
}

function setError(error: unknown, fallback: string): void {
  state = {
    ...state,
    lastError: error instanceof Error ? error.message : fallback,
  };
  notify();
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function replayEventsUntilSequence(
  roomId: string,
  minSequenceNumber: number,
): Promise<boolean> {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const response = await gameApi.getGameEvents(roomId);
    const events = response.success ? (response.data ?? []) : [];
    const latestSequence = events.reduce(
      (latest, event) => Math.max(latest, event.sequenceNumber),
      0,
    );

    if (latestSequence >= minSequenceNumber) {
      const replayed = replayGreatKingdomEvents(events);
      state = {
        ...state,
        gameState: replayed.gameState,
        lastAppliedSeq: replayed.lastAppliedSeq,
        lastError: null,
      };
      notify();
      return true;
    }

    await wait(250);
  }

  return false;
}

export function applyGreatKingdomEvent(
  gameState: GreatKingdomState | null,
  eventType: string,
  payload: Record<string, unknown>,
): GreatKingdomState | null {
  if (eventType === "init") {
    return payload.state as unknown as GreatKingdomState;
  }
  if (!gameState) return null;
  if (eventType === "great_kingdom_action") {
    const action = payload.action as unknown as GreatKingdomAction;
    const validation = validateAction(gameState, action);
    if (!validation.valid) return gameState;
    return reduceGameState(gameState, action);
  }
  console.warn(`Unknown great-kingdom event type: ${eventType}`);
  return gameState;
}

export function replayGreatKingdomEvents(
  events: readonly GameEvent[],
): ReplayResult {
  let gameState: GreatKingdomState | null = null;
  let lastAppliedSeq = 0;

  for (const event of events) {
    gameState = applyGreatKingdomEvent(
      gameState,
      event.eventType,
      event.payload as Record<string, unknown>,
    );
    lastAppliedSeq = event.sequenceNumber;
  }

  return { gameState, lastAppliedSeq };
}

export const greatKingdomMultiplayerActions = {
  initializeAsHost: async (
    roomId: string,
    participantUserIds: readonly string[],
    _config: Record<string, unknown> | undefined,
  ): Promise<void> => {
    const currentUserId = getCurrentUserId();
    state = {
      ...initialState,
      roomId,
      mySeatId: getSeatForUser(currentUserId, participantUserIds),
      isHost: true,
    };
    notify();

    try {
      const existingEventsResponse = await gameApi.getGameEvents(roomId);
      const existingEvents = existingEventsResponse.success
        ? (existingEventsResponse.data ?? [])
        : [];

      if (existingEvents.length > 0) {
        const replayed = replayGreatKingdomEvents(existingEvents);
        state = {
          ...state,
          gameState: replayed.gameState,
          lastAppliedSeq: replayed.lastAppliedSeq,
          lastError: null,
        };
        notify();
        return;
      }
    } catch (error) {
      setError(error, "게임 이벤트를 불러올 수 없습니다.");
      return;
    }

    try {
      const response = await gameApi.submitEvent(roomId, "init", {});
      if (response.success && response.data) {
        const synced = await replayEventsUntilSequence(
          roomId,
          response.data.sequenceNumber,
        );
        if (!synced) {
          state = {
            ...state,
            lastError: "서버 초기 상태 동기화가 지연되고 있습니다.",
          };
          notify();
        }
      }
    } catch (error) {
      setError(error, "게임 초기화에 실패했습니다.");
    }
  },

  initializeAsGuest: (
    roomId: string,
    participantUserIds: readonly string[],
  ): void => {
    const currentUserId = getCurrentUserId();
    state = {
      ...initialState,
      roomId,
      mySeatId: getSeatForUser(currentUserId, participantUserIds),
      isHost: false,
    };
    notify();
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const response = await gameApi.getGameEvents(roomId);
      if (!response.success || !response.data) return;

      const replayed = replayGreatKingdomEvents(response.data);
      state = {
        ...state,
        gameState: replayed.gameState,
        lastAppliedSeq: replayed.lastAppliedSeq,
        lastError: null,
      };
      notify();
    } catch (error) {
      setError(error, "게임 이벤트를 불러올 수 없습니다.");
    }
  },

  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    if (!state.gameState && eventType !== "init") return;

    state = {
      ...state,
      gameState: applyGreatKingdomEvent(state.gameState, eventType, payload),
      lastAppliedSeq: seq,
      lastError: null,
    };
    notify();
  },

  catchUpEvents: async (roomId: string): Promise<void> => {
    if (state.gameState?.phase === "game-over") return;
    if (catchUpInFlight) return;

    catchUpInFlight = true;
    try {
      const response = await gameApi.getGameEvents(
        roomId,
        state.lastAppliedSeq,
      );
      if (!response.success || !response.data) return;
      for (const event of response.data) {
        greatKingdomMultiplayerActions.applyRemoteEvent(
          event.eventType,
          event.payload as Record<string, unknown>,
          event.sequenceNumber,
        );
      }
    } catch {
      // Retry on the next poll or reconnect.
    } finally {
      catchUpInFlight = false;
    }
  },

  submitAction: async (
    action: GreatKingdomAction,
  ): Promise<{ readonly success: boolean; readonly error?: string }> => {
    if (!state.gameState || !state.roomId) {
      return { success: false, error: "게임이 아직 준비되지 않았습니다." };
    }
    if (state.mySeatId === null || action.playerId !== state.mySeatId) {
      const error = "관전자는 행동할 수 없습니다.";
      state = { ...state, lastError: error };
      notify();
      return { success: false, error };
    }

    const validation = validateAction(state.gameState, action);
    if (!validation.valid) {
      const error = validation.error ?? "지금은 실행할 수 없는 행동입니다.";
      state = { ...state, lastError: error };
      notify();
      return { success: false, error };
    }

    const roomId = state.roomId;
    try {
      const response = await gameApi.submitEvent(
        roomId,
        "great_kingdom_action",
        { action: action as unknown as Record<string, unknown> },
      );
      if (!response.success) {
        const error = response.error ?? "행동 전송에 실패했습니다.";
        state = { ...state, lastError: error };
        notify();
        void greatKingdomMultiplayerActions.catchUpEvents(roomId);
        return { success: false, error };
      }
      if (response.data) {
        greatKingdomMultiplayerActions.applyRemoteEvent(
          response.data.eventType,
          response.data.payload as Record<string, unknown>,
          response.data.sequenceNumber,
        );
      }
      return { success: true };
    } catch (error) {
      setError(error, "행동 전송에 실패했습니다.");
      return { success: false, error: "행동 전송에 실패했습니다." };
    }
  },

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    catchUpInFlight = false;
    state = { ...initialState };
    notify();
  },
};

export const greatKingdomMultiplayerStore = {
  getState: (): GreatKingdomMultiplayerState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useGreatKingdomMultiplayer(): GreatKingdomMultiplayerState {
  return useSyncExternalStore(
    greatKingdomMultiplayerStore.subscribe,
    greatKingdomMultiplayerStore.getState,
  );
}
